import { Cell } from '@/components/Studies/Groups/Analysis/Cell';
import Table from '@/components/UI/Table';
import type { TanStackCellProps, TanStackColumns } from '@/components/UI/Table/TableComponent.model';
import { _notNil } from '@/littledash';
import type { StudyApiId } from '@/model/Study.model';
import { type FC, useMemo } from 'react';
import { useAnalysisTable } from '@/utils/analysis/useAnalysis.tsx';
import { AnalysisRow } from '@/model/Analysis.model.ts';
import Loading from '@/components/Loading';
import ApiErrorBanner from '@/components/ApiErrorBanner';
import { ApiErrorType } from '@/components/ApiErrorBanner/ApiErrorBanner.tsx';

interface AnalysisTableProps {
  studyApiId: StudyApiId;
}

export const AnalysisTable: FC<AnalysisTableProps> = ({ studyApiId }) => {
  const { tableData, tableLoading, tableLoadingError } = useAnalysisTable();

  const columns = useMemo<TanStackColumns<AnalysisRow>>(() => {
    const tableType = tableData?.type;
    if (_notNil(tableType) && !tableLoading) {
      return (tableData?.columns ?? []).map((c) => ({
        id: String(c.title),
        accessorKey: c.accessor as keyof AnalysisRow,
        header: c.title,
        minSize: 150,
        enableResizing: true,
        cell: (props: TanStackCellProps<AnalysisRow>) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const row = (props as any).row;
          return (
            <Cell
              tableType={tableType}
              studyApiId={studyApiId}
              columnType={c.type}
              accessor={c.accessor as keyof AnalysisRow}
              data={row.original}
            />
          );
        },
      }));
    }
    return [];
  }, [studyApiId, tableData, tableLoading]);

  if (tableLoading) {
    return <Loading txt="Loading analysis" />;
  }

  if (tableLoadingError) {
    return (
      <div className="flex justify-center" data-test-component="AnalysisTable" data-test-element="container">
        <ApiErrorBanner title="There was a problem fetching analysis data" errorType={ApiErrorType.FETCH} />
      </div>
    );
  }

  return <Table columns={columns} data={tableData?.rows ?? []} />;
};
